import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { ReturnTypeEnum } from "../return";
import { SortDirection } from "../tables/table";
import {
  ArrayStringFilterBuilder,
  ArrayStringFilterDataSchema,
} from "./advanced-filters/array-string-filter";
import { BooleanFilterDataSchema } from "./advanced-filters/boolean-filter";
import {
  DateFilterBuilder,
  DateFilterDataSchema,
} from "./advanced-filters/date-filter";
import { ExistsFilterDataSchema } from "./advanced-filters/exists-filter";
import {
  FilterOperator,
  GenericFilterClause,
  GenericFilterGroup,
  isFilterGroup,
} from "./advanced-filters/generic-advanced-filter-data";
import {
  NumberFilterBuilder,
  NumberFilterDataSchema,
} from "./advanced-filters/number-filter";
import {
  StringFilterBuilder,
  StringFilterDataSchema,
} from "./advanced-filters/string-filter";

export const DEFAULT_PAGE_SIZE = 50;

export const mapReturnTypeToViewType = (returnType: ReturnTypeEnum) => {
  switch (returnType) {
    case ReturnTypeEnum.RETURN:
      return ViewTypesNames.RETURNS;
    case ReturnTypeEnum.CLAIM:
      return ViewTypesNames.CLAIMS;
    case ReturnTypeEnum.WARRANTY:
      return ViewTypesNames.WARRANTIES;
    case ReturnTypeEnum.RECYCLE:
      return ViewTypesNames.RECYCLE;
    default:
      assertNever(returnType);
  }
};

// Valid page sizes allowed by table2 component
export const VALID_PAGE_SIZES = [
  10, 25, 50, 100, 150, 200, 250, 500, 1000, 1500,
] as const;
export type ValidPageSize = (typeof VALID_PAGE_SIZES)[number];

export const ViewSortSchema = z.object({
  direction: createEnum(SortDirection, { enumName: "SortDirection" }),
  key: z.string(),
});
export type ViewSort = z.infer<typeof ViewSortSchema>;

export const ViewColumnSchema = z.object({
  key: z.string(),
  width: z.number(),
  hidden: z.boolean(),
  pinned: z.boolean().optional(),
});
export type ViewColumn = z.infer<typeof ViewColumnSchema>;

const AdvancedFilterDataSchema_ = z.discriminatedUnion("type", [
  DateFilterDataSchema,
  ArrayStringFilterDataSchema,
  NumberFilterDataSchema,
  StringFilterDataSchema,
  ExistsFilterDataSchema,
  BooleanFilterDataSchema,
]);
export const AdvancedFilterDataSchema: z.ZodType<AdvancedFilterData> =
  AdvancedFilterDataSchema_;
export type AdvancedFilterData = {} & z.infer<typeof AdvancedFilterDataSchema_>;

// Concrete filter types using the generic definitions
export type FilterClause = GenericFilterClause<AdvancedFilterData>;
export type FilterGroup = GenericFilterGroup<AdvancedFilterData>;

const FilterGroupSchema_: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({
    operator: createEnum(FilterOperator, { enumName: "FilterOperator" }),
    clauses: z.array(FilterClauseSchema),
  }),
);
export const FilterGroupSchema: z.ZodType<FilterGroup> = FilterGroupSchema_;

const FilterClauseSchema_: z.ZodType<FilterClause> = z.lazy(() =>
  z.union([AdvancedFilterDataSchema, FilterGroupSchema]),
);
export const FilterClauseSchema: z.ZodType<FilterClause> = FilterClauseSchema_;

const BaseViewSchema_ = z.object({
  _id: z.string().nullish(),
  table: z.string(),
  name: z.string(),
  filters: z.array(AdvancedFilterDataSchema),
  filterGroup: FilterGroupSchema.nullish(),
  columns: z.array(ViewColumnSchema),
  sort: ViewSortSchema.nullish(),
  pageSize: z.number().nullish(),
  folderId: z.string().nullish(),
  position: z.number(),
  deleted: z.boolean().nullish(),
  isNotEditable: z.boolean().optional(),
});
export const BaseViewSchema: z.ZodType<BaseView> = BaseViewSchema_;
export interface BaseView extends z.infer<typeof BaseViewSchema_> {}

export function countFilterClauses(group: FilterGroup): number {
  let count = 0;
  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      count += countFilterClauses(clause);
    } else {
      count += 1;
    }
  }
  return count;
}

export function getFilterCount(view: BaseView): number {
  if (view.filterGroup) {
    return countFilterClauses(view.filterGroup);
  }
  return view.filters.length;
}

/**
 * Document-level schema representing how views are stored in MongoDB.
 * The view data is nested under the `view` field.
 */
export const ViewDocumentSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  table: z.string(),
  name: z.string(),
  view: BaseViewSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type ViewDocument = z.infer<typeof ViewDocumentSchema>;

const BaseViewWithNotificationsSchema_ = BaseViewSchema_.extend({
  showNotification: z.boolean().default(false),
  hideWhenEmpty: z.boolean().optional(),
});
export const BaseViewWithNotificationsSchema: z.ZodType<BaseViewWithNotifications> =
  BaseViewWithNotificationsSchema_;
export interface BaseViewWithNotifications
  extends z.infer<typeof BaseViewWithNotificationsSchema_> {}

const PickSessionPriorityViewSchema_ = BaseViewSchema_.extend({
  percentage: z.number().min(0).max(100),
});
export const PickSessionPriorityViewSchema: z.ZodType<PickSessionPriorityView> =
  PickSessionPriorityViewSchema_;
export interface PickSessionPriorityView
  extends z.infer<typeof PickSessionPriorityViewSchema_> {}

// Map view types to a Mongoose model and a Typescript interface
export const viewSchemas = {
  "fulfillment-groups": BaseViewSchema,
  "bulk-fulfillments": BaseViewSchema,
  "pick-session": BaseViewSchema,
  "pick-session-priority": PickSessionPriorityViewSchema,
  returns: BaseViewWithNotificationsSchema,
  claims: BaseViewWithNotificationsSchema,
  warranties: BaseViewWithNotificationsSchema,
  recycle: BaseViewWithNotificationsSchema,
  registrations: BaseViewSchema,
  "purchase-orders": BaseViewSchema,
  shipments: BaseViewSchema,
  transfers: BaseViewSchema,
  "inventory-levels": BaseViewSchema,
  forecasting: BaseViewSchema,
  "catalog-products": BaseViewSchema,
  "order-tracking-shipments": BaseViewSchema,
  "work-orders": BaseViewSchema,
};

export enum ViewTypesNames {
  FULFILLMENT_GROUPS = "fulfillment-groups",
  BULK_FULFILLMENTS = "bulk-fulfillments",
  PICK_SESSION = "pick-session",
  PICK_SESSION_PRIORITY = "pick-session-priority",
  RETURNS = "returns",
  CLAIMS = "claims",
  WARRANTIES = "warranties",
  RECYCLE = "recycle",
  REGISTRATIONS = "registrations",
  PURCHASE_ORDERS = "purchase-orders",
  SHIPMENTS = "shipments",
  TRANSFERS = "transfers",
  INVENTORY_LEVELS = "inventory-levels",
  FORECASTING = "forecasting",
  CATALOG_PRODUCTS = "catalog-products",
  ORDER_TRACKING_SHIPMENTS = "order-tracking-shipments",
  WORK_ORDERS = "work-orders",
}

export type viewTypes = {
  [key in keyof typeof viewSchemas]: z.infer<(typeof viewSchemas)[key]>;
};

export type FilterBuilder =
  | typeof DateFilterBuilder
  | typeof ArrayStringFilterBuilder
  | typeof NumberFilterBuilder
  | typeof StringFilterBuilder;

const baseViewWithIdSchema_ = BaseViewSchema_.extend({
  _id: z.string(),
  updatedAt: z.coerce.date().nullish(),
});
const baseViewWithIdSchema: z.ZodType<BaseViewWithId> = baseViewWithIdSchema_;
export interface BaseViewWithId extends z.infer<typeof baseViewWithIdSchema_> {}

const baseViewWithNotificationsAndIdSchema_ =
  BaseViewWithNotificationsSchema_.extend({ _id: z.string() });
export const baseViewWithNotificationsAndIdSchema: z.ZodType<BaseViewWithNotificationsAndId> =
  baseViewWithNotificationsAndIdSchema_;
export interface BaseViewWithNotificationsAndId
  extends z.infer<typeof baseViewWithNotificationsAndIdSchema_> {}

const pickSessionPriorityViewWithIdSchema_ =
  PickSessionPriorityViewSchema_.extend({ _id: z.string() });
export const pickSessionPriorityViewWithIdSchema: z.ZodType<PickSessionPriorityViewWithId> =
  pickSessionPriorityViewWithIdSchema_;
export interface PickSessionPriorityViewWithId
  extends z.infer<typeof pickSessionPriorityViewWithIdSchema_> {}

export const viewWithIdSchemas = {
  "fulfillment-groups": baseViewWithIdSchema,
  "bulk-fulfillments": baseViewWithIdSchema,
  "pick-session": baseViewWithIdSchema,
  "pick-session-priority": pickSessionPriorityViewWithIdSchema,
  returns: baseViewWithNotificationsAndIdSchema,
  claims: baseViewWithNotificationsAndIdSchema,
  warranties: baseViewWithNotificationsAndIdSchema,
  recycle: baseViewWithNotificationsAndIdSchema,
  registrations: baseViewWithIdSchema,
  "purchase-orders": baseViewWithIdSchema,
  shipments: baseViewWithIdSchema,
  transfers: baseViewWithIdSchema,
  "inventory-levels": baseViewWithIdSchema,
  forecasting: baseViewWithIdSchema,
  "catalog-products": baseViewWithIdSchema,
  "order-tracking-shipments": baseViewWithIdSchema,
  "work-orders": baseViewWithIdSchema,
};

export type viewWithIdTypes = {
  [key in keyof typeof viewWithIdSchemas]: z.infer<
    (typeof viewWithIdSchemas)[key]
  >;
};

export type FulfillmentGroupView =
  viewWithIdTypes[ViewTypesNames.FULFILLMENT_GROUPS];

export type BulkFulfillmentView =
  viewWithIdTypes[ViewTypesNames.BULK_FULFILLMENTS];

export type PickSessionView = viewWithIdTypes[ViewTypesNames.PICK_SESSION];
export const PickSessionViewSchema =
  viewWithIdSchemas[ViewTypesNames.PICK_SESSION];

export type ReturnView = viewWithIdTypes[ViewTypesNames.RETURNS];
export type ClaimView = viewWithIdTypes[ViewTypesNames.CLAIMS];
export type WarrantyView = viewWithIdTypes[ViewTypesNames.WARRANTIES];
export type RecycleView = viewWithIdTypes[ViewTypesNames.RECYCLE];
export type RegistrationView = viewWithIdTypes[ViewTypesNames.REGISTRATIONS];
export type PurchaseOrderView = viewWithIdTypes[ViewTypesNames.PURCHASE_ORDERS];
export type ShipmentView = viewWithIdTypes[ViewTypesNames.SHIPMENTS];
export type TransferView = viewWithIdTypes[ViewTypesNames.TRANSFERS];
export type InventoryLevelsView =
  viewWithIdTypes[ViewTypesNames.INVENTORY_LEVELS];
export type ForecastingView = viewWithIdTypes[ViewTypesNames.FORECASTING];

export const ClientFulfillmentGroupView =
  viewWithIdSchemas["fulfillment-groups"];
export type ClientFulfillmentGroupView = z.infer<
  typeof ClientFulfillmentGroupView
>;

export const ClientBulkFulfillmentView = viewWithIdSchemas["bulk-fulfillments"];
export type ClientBulkFulfillmentView = z.infer<
  typeof ClientBulkFulfillmentView
>;

export const ClientReturnView = viewWithIdSchemas.returns;
export type ClientReturnView = z.infer<typeof ClientReturnView>;
export const ClientClaimView = viewWithIdSchemas.claims;
export type ClientClaimView = z.infer<typeof ClientClaimView>;
export const ClientWarrantyView = viewWithIdSchemas.warranties;
export type ClientWarrantyView = z.infer<typeof ClientWarrantyView>;
export const ClientRecycleView = viewWithIdSchemas.recycle;
export type ClientRecycleView = z.infer<typeof ClientRecycleView>;
export const ClientRegistrationView = viewWithIdSchemas.registrations;
export type ClientRegistrationView = z.infer<typeof ClientRegistrationView>;
export const ClientPurchaseOrdersView = viewWithIdSchemas["purchase-orders"];
export type ClientPurchaseOrdersView = z.infer<typeof ClientPurchaseOrdersView>;
export const ClientShipmentView = viewWithIdSchemas.shipments;
export type ClientShipmentView = z.infer<typeof ClientShipmentView>;
export const ClientInventoryLevelsView = viewWithIdSchemas["inventory-levels"];
export type ClientInventoryLevelsView = z.infer<
  typeof ClientInventoryLevelsView
>;
export const ClientForecastingView = viewWithIdSchemas.forecasting;
export type ClientForecastingView = z.infer<typeof ClientForecastingView>;

export type CatalogProductsView =
  viewWithIdTypes[ViewTypesNames.CATALOG_PRODUCTS];
export const ClientCatalogProductsView = viewWithIdSchemas["catalog-products"];
export type ClientCatalogProductsView = z.infer<
  typeof ClientCatalogProductsView
>;

export type OrderTrackingShipmentView =
  viewWithIdTypes[ViewTypesNames.ORDER_TRACKING_SHIPMENTS];
export const ClientOrderTrackingShipmentView =
  viewWithIdSchemas["order-tracking-shipments"];
export type ClientOrderTrackingShipmentView = z.infer<
  typeof ClientOrderTrackingShipmentView
>;

export type WorkOrderView = viewWithIdTypes[ViewTypesNames.WORK_ORDERS];
export const ClientWorkOrderView = viewWithIdSchemas["work-orders"];
export type ClientWorkOrderView = z.infer<typeof ClientWorkOrderView>;

export { FilterOperator } from "./advanced-filters/generic-advanced-filter-data";
